/**
 * Barra de envio — desenhada em cima do WhatsApp, não "inspirada" nele.
 *
 * O desenho foi medido nos prints do WhatsApp no iPhone do proprietário
 * (22/09/2026):
 *   [+]  ( ________________ 🙂 )  [câmera]  (🎤)
 *   solto   pílula com o texto e o   solto     círculo
 *   à esq.  ícone DENTRO, à direita            cheio à dir.
 *
 * Gravando, a barra inteira troca: microfone vermelho, o tempo correndo,
 * "Deslize para cancelar ‹" no meio, e o cadeado logo acima do botão verde.
 *
 * O botão da direita troca de microfone para avião assim que há texto — é o
 * gesto que todo mundo já tem na memória, e memória muscular vale mais do que
 * qualquer tela nova.
 *
 * GRAVAÇÃO, as três formas que o WhatsApp aceita:
 *   - segurar e soltar    -> grava enquanto segura, envia ao soltar
 *   - arrastar para cima  -> TRAVA a gravação, mãos livres
 *   - arrastar para o lado-> cancela, não envia nada
 * A primeira sozinha não basta: gravar recado de um minuto com o dedo preso na
 * tela é o tipo de coisa que ninguém faz duas vezes.
 */
import { useEffect, useRef, useState } from 'react';
import { post, getCsrfToken } from '../lib/api.js';
import { useSession } from '../lib/session.js';
import { useResource } from '../lib/useResource.js';
import { aplicarVariaveis, consultaDeAtalho, filtrarRespostas, type RespostaRapida } from '../lib/respostas.js';
import { ErrorBox, Field, Modal, useSubmit } from './ui.js';
import {
  IconeMaisFino,
  IconeEmoji,
  IconeMicrofone,
  IconeEnviar,
  IconeCamera,
  IconeImagem,
  IconeDocumento,
  IconeContato,
  IconeLocalizacao,
  IconeLixeira,
  IconeCadeado,
} from './Icones.js';

interface Anexo {
  id: string;
  kind: 'image' | 'audio' | 'document' | 'contact';
  mimeType: string;
  filename: string | null;
  sizeBytes: number;
  durationSeconds: number | null;
}

/** Os mais usados num atendimento. Sem biblioteca: são 48 caracteres. */
const EMOJIS = [
  '😀','😅','😊','😍','🤝','👍','👌','🙏','👏','💪','🎉','❤️',
  '😂','😉','🤔','😐','😕','😢','😮','😴','🥳','🤯','😎','🫡',
  '🚗','🛞','🔧','🛠️','⛽','🔋','🧰','📅','⏰','📍','📄','💰',
  '✅','❌','⚠️','🔥','⭐','💯','👋','🙌','☝️','👇','➡️','🕐',
];

export function Compositor({
  conversationId,
  onEnviou,
  nomeDoCliente = null,
}: {
  conversationId: string;
  onEnviou: () => void;
  /** Para {cliente} nas mensagens prontas. */
  nomeDoCliente?: string | null;
}) {
  const [texto, setTexto] = useState('');
  const { me } = useSession();

  /**
   * Mensagens prontas: "/" no começo da caixa abre a lista, como no
   * computador da loja (pedido do Guel, 06/10/2026). Setas escolhem, Enter ou
   * Tab colocam o texto na caixa — para ajustar o valor antes de enviar.
   */
  const respostas = useResource<{ respostas: RespostaRapida[] }>('/api/respostas-rapidas', []);
  const consulta = consultaDeAtalho(texto);
  const opcoes = consulta === null ? [] : filtrarRespostas(respostas.data?.respostas ?? [], consulta).slice(0, 8);
  const [escolhida, setEscolhida] = useState(0);
  useEffect(() => setEscolhida(0), [consulta]);
  function usarResposta(r: RespostaRapida) {
    setTexto(aplicarVariaveis(r.texto, { atendente: me?.name, cliente: nomeDoCliente }));
    requestAnimationFrame(() => areaTexto.current?.focus());
  }
  const [anexo, setAnexo] = useState<Anexo | null>(null);
  const [folha, setFolha] = useState(false);
  const [emojis, setEmojis] = useState(false);
  const [contato, setContato] = useState(false);
  const [erro, setErro] = useState<unknown>(null);
  const [subindo, setSubindo] = useState(false);

  const campoFoto = useRef<HTMLInputElement>(null);
  const campoCamera = useRef<HTMLInputElement>(null);
  const campoArquivo = useRef<HTMLInputElement>(null);
  const areaTexto = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const el = areaTexto.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 110)}px`;
  }, [texto]);

  async function subirArquivo(arquivo: File, duracaoSegundos?: number) {
    setErro(null);
    setSubindo(true);
    try {
      const dados = new FormData();
      dados.append('arquivo', arquivo, arquivo.name || 'anexo');
      if (duracaoSegundos) dados.append('duracaoSegundos', String(Math.round(duracaoSegundos)));
      const resposta = await fetch(`/api/conversations/${conversationId}/anexos`, {
        method: 'POST',
        body: dados,
        credentials: 'same-origin',
        headers: { 'x-csrf-token': getCsrfToken() ?? '' },
      });
      if (!resposta.ok) {
        const corpo = (await resposta.json().catch(() => null)) as
          | { error?: { message?: string } }
          | null;
        throw new Error(corpo?.error?.message ?? `Falha ao subir o arquivo (${resposta.status}).`);
      }
      const { anexo: guardado } = (await resposta.json()) as { anexo: Anexo };
      setFolha(false);
      // Áudio vai direto: quem gravou um recado já decidiu mandar.
      if (guardado.kind === 'audio') {
        await post(`/api/conversations/${conversationId}/messages`, {
          kind: 'audio',
          attachmentId: guardado.id,
        });
        onEnviou();
      } else {
        setAnexo(guardado);
      }
    } catch (e) {
      setErro(e);
    } finally {
      setSubindo(false);
    }
  }

  const enviar = useSubmit(async () => {
    if (anexo) {
      await post(`/api/conversations/${conversationId}/messages`, {
        kind: anexo.kind,
        attachmentId: anexo.id,
        body: texto.trim() || undefined,
      });
    } else if (texto.trim()) {
      await post(`/api/conversations/${conversationId}/messages`, {
        kind: 'text',
        body: texto.trim(),
      });
    } else {
      return;
    }
    setTexto('');
    setAnexo(null);
    onEnviou();
  });

  const mandarLocalizacao = useSubmit(async () => {
    await post(`/api/conversations/${conversationId}/localizacao`, {});
    setFolha(false);
    onEnviou();
  });

  const temConteudo = Boolean(texto.trim() || anexo);
  const [gravando, setGravando] = useState(false);

  return (
    <div className={`wa-barra ${gravando ? 'gravando' : ''}`}>
      <ErrorBox error={erro ?? enviar.error ?? mandarLocalizacao.error} />

      {anexo ? (
        <div className="wa-anexo">
          <span className="wa-anexo-icone">
            {anexo.kind === 'image' ? <IconeImagem size={20} /> : <IconeDocumento size={20} />}
          </span>
          <div className="wa-anexo-nome">
            {anexo.filename ?? 'Arquivo'}
            <small>{(anexo.sizeBytes / 1024).toFixed(0)} KB</small>
          </div>
          <button type="button" className="wa-icone" aria-label="Tirar o anexo" onClick={() => setAnexo(null)}>
            <IconeLixeira size={20} />
          </button>
        </div>
      ) : null}

      {opcoes.length > 0 ? (
        <div className="wa-prontas" role="listbox" aria-label="Mensagens prontas">
          {opcoes.map((r, i) => (
            <button
              key={r.id}
              type="button"
              role="option"
              aria-selected={i === escolhida}
              className={i === escolhida ? 'ativa' : ''}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => usarResposta(r)}
            >
              <strong>/{r.atalho}</strong> <span>{r.titulo}</span>
              <small>{r.texto.replace(/\s+/g, ' ').slice(0, 90)}</small>
            </button>
          ))}
        </div>
      ) : null}

      {emojis ? (
        <div className="wa-emojis" role="listbox" aria-label="Emojis">
          {EMOJIS.map((e) => (
            <button
              key={e}
              type="button"
              onClick={() => {
                setTexto((t) => t + e);
                areaTexto.current?.focus();
              }}
            >
              {e}
            </button>
          ))}
        </div>
      ) : null}

      <div className="wa-linha">
        <button
          type="button"
          className="wa-icone"
          aria-label="Anexar"
          aria-expanded={folha}
          onClick={() => {
            setFolha((v) => !v);
            setEmojis(false);
          }}
        >
          <IconeMaisFino />
        </button>

        <div className="wa-pilula">
          <textarea
            ref={areaTexto}
            value={texto}
            rows={1}
            placeholder={anexo ? 'Legenda' : 'Mensagem'}
            onChange={(e) => setTexto(e.target.value)}
            onKeyDown={(e) => {
              if (opcoes.length > 0) {
                if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                  e.preventDefault();
                  const passo = e.key === 'ArrowDown' ? 1 : -1;
                  setEscolhida((i) => (i + passo + opcoes.length) % opcoes.length);
                  return;
                }
                if (e.key === 'Enter' || e.key === 'Tab') {
                  e.preventDefault();
                  usarResposta(opcoes[Math.min(escolhida, opcoes.length - 1)]!);
                  return;
                }
                if (e.key === 'Escape') {
                  e.preventDefault();
                  setTexto('');
                  return;
                }
              }
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                void enviar.submit();
              }
            }}
          />
          <button
            type="button"
            className="wa-icone wa-icone-dentro"
            aria-label="Emojis"
            aria-expanded={emojis}
            onClick={() => {
              setEmojis((v) => !v);
              setFolha(false);
            }}
          >
            <IconeEmoji size={22} />
          </button>
        </div>

        {!temConteudo ? (
          <button
            type="button"
            className="wa-icone"
            aria-label="Câmera"
            onClick={() => campoCamera.current?.click()}
          >
            <IconeCamera />
          </button>
        ) : null}

        {temConteudo ? (
          <button
            type="button"
            className="wa-redondo"
            aria-label="Enviar"
            disabled={enviar.saving || subindo}
            onClick={() => void enviar.submit()}
          >
            <IconeEnviar size={21} />
          </button>
        ) : (
          <Gravador
            aoGravar={(arq, seg) => void subirArquivo(arq, seg)}
            aoMudarEstado={setGravando}
          />
        )}
      </div>

      {folha ? (
        <>
          <div className="wa-fundo" onClick={() => setFolha(false)} />
          <div className="wa-folha" role="menu" aria-label="Anexar">
            <span className="wa-alca" aria-hidden />
            <div className="wa-grade">
              <button type="button" onClick={() => campoFoto.current?.click()}>
                <span className="wa-bolha azul"><IconeImagem /></span> Fotos
              </button>
              <button type="button" onClick={() => campoCamera.current?.click()}>
                <span className="wa-bolha branco"><IconeCamera /></span> Câmera
              </button>
              <button type="button" onClick={() => void mandarLocalizacao.submit()}>
                <span className="wa-bolha verde"><IconeLocalizacao /></span> Localização
              </button>
              <button
                type="button"
                onClick={() => {
                  setFolha(false);
                  setContato(true);
                }}
              >
                <span className="wa-bolha branco"><IconeContato /></span> Contato
              </button>
              <button type="button" onClick={() => campoArquivo.current?.click()}>
                <span className="wa-bolha azul"><IconeDocumento /></span> Documento
              </button>
            </div>
          </div>
        </>
      ) : null}

      {contato ? (
        <FormularioDeContato
          conversationId={conversationId}
          onFechar={() => setContato(false)}
          onEnviou={() => {
            setContato(false);
            onEnviou();
          }}
        />
      ) : null}

      <input
        ref={campoFoto}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void subirArquivo(f);
          e.target.value = '';
        }}
      />
      <input
        ref={campoCamera}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void subirArquivo(f);
          e.target.value = '';
        }}
      />
      <input
        ref={campoArquivo}
        type="file"
        accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
        hidden
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) void subirArquivo(f);
          e.target.value = '';
        }}
      />
      {subindo ? <p className="hint">Enviando…</p> : null}
    </div>
  );
}

/** Quanto o dedo precisa subir para travar, e andar para o lado para cancelar. */
const TRAVAR_PX = 60;
const CANCELAR_PX = 90;

function Gravador({
  aoGravar,
  aoMudarEstado,
}: {
  aoGravar: (arquivo: File, segundos: number) => void;
  /** Avisa a barra para esconder a pílula enquanto grava. */
  aoMudarEstado: (gravando: boolean) => void;
}) {
  const [estado, setEstado] = useState<'parado' | 'gravando' | 'travado'>('parado');
  const [segundos, setSegundos] = useState(0);
  const [subiu, setSubiu] = useState(0);
  const [erro, setErro] = useState<string | null>(null);

  const gravador = useRef<MediaRecorder | null>(null);
  const pedacos = useRef<Blob[]>([]);
  const inicio = useRef(0);
  const partida = useRef({ x: 0, y: 0 });
  const cancelado = useRef(false);
  /**
   * O dedo pode subir ANTES de o navegador liberar o microfone: pedir permissão
   * e ligar o gravador leva algumas centenas de milissegundos, e um toque rápido
   * acaba nesse intervalo. Sem esta marca, o `onstop` nunca chegava e o botão
   * ficava preso em "gravando" — era o áudio que não fazia nada.
   */
  const soltouAntes = useRef(false);
  /** Estado em ref: o `onpointerup` do window lê o valor de agora, não o da
   *  renderização em que foi registrado. */
  const estadoRef = useRef<'parado' | 'gravando' | 'travado'>('parado');

  function mudar(novo: 'parado' | 'gravando' | 'travado') {
    estadoRef.current = novo;
    setEstado(novo);
    aoMudarEstado(novo !== 'parado');
  }

  useEffect(() => {
    if (estado === 'parado') return;
    const t = setInterval(() => setSegundos(Math.round((Date.now() - inicio.current) / 1000)), 250);
    return () => clearInterval(t);
  }, [estado]);

  function encerrar(cancelar: boolean) {
    cancelado.current = cancelar;
    const rec = gravador.current;
    gravador.current = null;
    if (rec && rec.state !== 'inactive') rec.stop();
    else mudar('parado');
  }

  /**
   * O dedo pode sair do botão, ou o toque acabar fora dele. Escutar no window
   * é o que faz "soltar para enviar" funcionar mesmo quando a mão escorrega —
   * e é assim que o WhatsApp se comporta.
   */
  useEffect(() => {
    if (estado !== 'gravando') return;
    const mover = (e: PointerEvent) => {
      const dx = partida.current.x - e.clientX;
      const dy = partida.current.y - e.clientY;
      setSubiu(dy);
      if (dx > CANCELAR_PX) encerrar(true);
      else if (dy > TRAVAR_PX) mudar('travado');
    };
    const soltar = () => {
      if (estadoRef.current === 'gravando') encerrar(false);
    };
    window.addEventListener('pointermove', mover);
    window.addEventListener('pointerup', soltar);
    window.addEventListener('pointercancel', soltar);
    return () => {
      window.removeEventListener('pointermove', mover);
      window.removeEventListener('pointerup', soltar);
      window.removeEventListener('pointercancel', soltar);
    };
  }, [estado]);

  async function comecar(x: number, y: number) {
    setErro(null);
    cancelado.current = false;
    soltouAntes.current = false;
    partida.current = { x, y };
    setSubiu(0);
    setSegundos(0);

    let fluxo: MediaStream;
    try {
      fluxo = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      setErro('Libere o microfone nas permissões do navegador.');
      mudar('parado');
      return;
    }

    // Soltou enquanto o navegador pedia permissão: nada a gravar.
    if (soltouAntes.current) {
      fluxo.getTracks().forEach((t) => t.stop());
      mudar('parado');
      return;
    }

    let rec: MediaRecorder;
    try {
      rec = new MediaRecorder(fluxo);
    } catch {
      fluxo.getTracks().forEach((t) => t.stop());
      setErro('Este navegador não grava áudio.');
      mudar('parado');
      return;
    }

    pedacos.current = [];
    rec.ondataavailable = (e) => e.data.size > 0 && pedacos.current.push(e.data);
    rec.onstop = () => {
      const duracao = (Date.now() - inicio.current) / 1000;
      fluxo.getTracks().forEach((t) => t.stop());
      mudar('parado');
      setSubiu(0);
      if (cancelado.current || duracao < 0.6 || pedacos.current.length === 0) return;
      const tipo = rec.mimeType || 'audio/webm';
      const extensao = tipo.includes('mp4') ? 'm4a' : tipo.includes('ogg') ? 'ogg' : 'webm';
      aoGravar(new File(pedacos.current, `recado.${extensao}`, { type: tipo }), duracao);
    };

    inicio.current = Date.now();
    rec.start();
    gravador.current = rec;
    mudar('gravando');
  }

  if (erro) {
    return (
      <button type="button" className="wa-redondo alerta" title={erro} onClick={() => setErro(null)}>
        <IconeMicrofone size={21} />
      </button>
    );
  }

  if (estado === 'travado') {
    return (
      <div className="wa-travado">
        <button type="button" className="wa-icone" aria-label="Cancelar" onClick={() => encerrar(true)}>
          <IconeLixeira size={21} />
        </button>
        <span className="wa-tempo">
          <i className="wa-ponto" /> {formatarTempo(segundos)}
        </span>
        <button
          type="button"
          className="wa-redondo"
          aria-label="Enviar áudio"
          onClick={() => encerrar(false)}
        >
          <IconeEnviar size={21} />
        </button>
      </div>
    );
  }

  return (
    <>
      {estado === 'gravando' ? (
        <>
          <div className={`wa-cadeado ${subiu > 20 ? 'perto' : ''}`} aria-hidden>
            <IconeCadeado size={18} />
            <span className="wa-seta">⌃</span>
          </div>
          <div className="wa-gravando" aria-live="polite">
            <span className="wa-ponto" />
            <span className="wa-tempo">{formatarTempo(segundos)}</span>
            <span className="wa-dica">
              {subiu > 20 ? 'Solte para travar' : 'Deslize para cancelar ‹'}
            </span>
          </div>
        </>
      ) : null}
      <button
        type="button"
        className={`wa-redondo wa-icone-gravando ${estado === 'gravando' ? 'gravando' : ''}`}
        aria-label="Segure para gravar áudio"
        // `touch-action: none` no CSS impede o navegador de tratar o gesto como
        // rolagem e engolir o pointermove no meio da gravação.
        onPointerDown={(e) => {
          e.preventDefault();
          void comecar(e.clientX, e.clientY);
        }}
        onPointerUp={() => {
          soltouAntes.current = true;
          if (estadoRef.current === 'gravando') encerrar(false);
        }}
      >
        <IconeMicrofone size={21} />
      </button>
    </>
  );
}

function formatarTempo(s: number): string {
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

function FormularioDeContato({
  conversationId,
  onFechar,
  onEnviou,
}: {
  conversationId: string;
  onFechar: () => void;
  onEnviou: () => void;
}) {
  const [nome, setNome] = useState('');
  const [telefone, setTelefone] = useState('');
  const enviar = useSubmit(async () => {
    await post(`/api/conversations/${conversationId}/messages`, {
      kind: 'contact',
      contact: { name: nome.trim(), phone: telefone.trim(), organization: 'Auto Center GL' },
    });
    onEnviou();
  });

  return (
    <Modal title="Enviar contato" onClose={onFechar}>
      <ErrorBox error={enviar.error} />
      <Field label="Nome" required>
        <input value={nome} onChange={(e) => setNome(e.target.value)} />
      </Field>
      <Field label="Telefone" required hint="Com DDD. Ex.: +55 71 98424-3307">
        <input
          value={telefone}
          inputMode="tel"
          onChange={(e) => setTelefone(e.target.value)}
        />
      </Field>
      <button
        type="button"
        className="button button-primary"
        disabled={enviar.saving || nome.trim().length < 2 || telefone.trim().length < 8}
        onClick={() => void enviar.submit()}
      >
        {enviar.saving ? 'Enviando…' : 'Enviar contato'}
      </button>
    </Modal>
  );
}
